import { forwardRef } from 'react'
import type { Presupuesto, PresupuestoItem, Servicio } from '@/types/database'
import type { ConfiguracionEstudio } from '@/services/configuracionService'
import { formatDate } from '@/lib/utils'

interface PresupuestoTemplateProps {
  presupuesto: Presupuesto
  servicios: Servicio[] // Maestro de servicios para lookup
  configEstudio?: ConfiguracionEstudio | null
  organizacion?: {
    nombre?: string | null
    branding?: {
      logo_light_url?: string | null
      logo_dark_url?: string | null
    } | null
  } | null
}

// Helper to safely parse items from presupuesto
function parseItems(presupuesto: Presupuesto): PresupuestoItem[] {
  if (!presupuesto.items) {
    return []
  }
  if (typeof presupuesto.items === 'string') {
    try {
      return JSON.parse(presupuesto.items) as PresupuestoItem[]
    } catch {
      return []
    }
  }
  return presupuesto.items as unknown as PresupuestoItem[]
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
  }).format(value)
}

// Format CUIT as XX-XXXXXXXX-X
function formatCuit(cuit: string | null): string {
  if (!cuit) return '-'
  const clean = cuit.replace(/\D/g, '')
  if (clean.length === 11) {
    return `${clean.slice(0, 2)}-${clean.slice(2, 10)}-${clean.slice(10)}`
  }
  return cuit
}

// Detect if CUIT belongs to a juridica entity (30, 33, 34 prefix)
function esPersonaJuridica(cuit: string | null): boolean {
  if (!cuit) return false
  const clean = cuit.replace(/\D/g, '')
  return clean.startsWith('30') || clean.startsWith('33') || clean.startsWith('34')
}

// Helper to lookup service details
function getServicioDetails(servicioId: string, servicios: Servicio[]): { nombre: string; descripcion: string | null } {
  const servicio = servicios.find(s => s.id === servicioId)
  return {
    nombre: servicio?.nombre ?? 'Servicio no encontrado',
    descripcion: servicio?.descripcion ?? null
  }
}

export const PresupuestoTemplate = forwardRef<HTMLDivElement, PresupuestoTemplateProps>(
  ({ presupuesto, servicios, configEstudio, organizacion }, ref) => {
    const items = parseItems(presupuesto)
    const numeroFormateado = (presupuesto.numero_presupuesto || 0).toString().padStart(4, '0')

    const fechaValidez = new Date(presupuesto.fecha || new Date())
    fechaValidez.setDate(fechaValidez.getDate() + (presupuesto.validez_dias ?? 15))

    const subtotalItems = items.reduce((acc, item) => acc + (item?.subtotal ?? 0), 0)
    const descuentoPorcentaje = presupuesto.descuento_porcentaje ?? 0
    const ivaPorcentaje = presupuesto.iva_porcentaje ?? 0
    const descuento = subtotalItems * (descuentoPorcentaje / 100)
    const baseImponible = subtotalItems - descuento
    const iva = baseImponible * (ivaPorcentaje / 100)
    const totalCalculado = baseImponible + iva
    // Usar total del presupuesto si existe, sino calcular
    const totalFinal = (presupuesto.total || 0) > 0 ? presupuesto.total : totalCalculado

    // Verificar si algún item tiene descuento para mostrar/ocultar columna
    const hayDescuentoEnItems = items.some(item => (item.descuento_porcentaje ?? 0) > 0)

    // Datos del estudio (desde configuracion o defaults)
    const estudioNombre = configEstudio?.nombre || organizacion?.nombre || 'Mi Estudio Contable'
    const estudioCuit = configEstudio?.cuit || ''
    const estudioTelefono = configEstudio?.telefono || ''
    const estudioEmail = configEstudio?.email || ''
    const estudioSitioWeb = configEstudio?.sitio_web || ''
    const estudioResponsable = configEstudio?.responsable_nombre || ''
    const estudioMatricula = configEstudio?.matricula_profesional || ''
    const textoLegal = configEstudio?.texto_legal_presupuestos ||
      `Presupuesto valido por ${presupuesto.validez_dias ?? 15} dias. Los importes podran modificarse si cambia la normativa fiscal o el alcance del servicio requerido.`

    // Logo del estudio - preferir dark para el header oscuro
    const logoUrlHeader = organizacion?.branding?.logo_dark_url || organizacion?.branding?.logo_light_url || null
    const logoUrlFooter = organizacion?.branding?.logo_light_url || organizacion?.branding?.logo_dark_url || null

    return (
      <div
        ref={ref}
        style={{
          width: '794px', // A4 at 96 DPI
          height: '1123px', // A4 at 96 DPI
          maxHeight: '1123px',
          backgroundColor: '#ffffff',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          overflow: 'hidden',
        }}
      >
        <article style={{ display: 'flex', flexDirection: 'column', height: '1123px' }}>
          {/* Header - reduced padding */}
          <header
            style={{
              position: 'relative',
              overflow: 'hidden',
              padding: '20px 24px 16px',
              color: 'white',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 65%, rgba(234, 126, 52, 0.9) 115%)',
            }}
          >
            <div style={{ position: 'relative', zIndex: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {logoUrlHeader ? (
                  <img src={logoUrlHeader} alt={estudioNombre} style={{ height: '36px', width: 'auto' }} crossOrigin="anonymous" />
                ) : (
                  <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{estudioNombre}</div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', fontSize: '12px', color: 'rgba(255,255,255,0.9)' }}>
                <span style={{ fontWeight: 600 }}>Presupuesto #{numeroFormateado}</span>
                <span>Fecha: {formatDate(presupuesto.fecha || new Date().toISOString())}</span>
                <span>Valido hasta: {formatDate(fechaValidez.toISOString().split('T')[0])}</span>
              </div>
            </div>
          </header>

          {/* Body - reduced padding and gaps */}
          <div
            style={{
              flex: 1,
              padding: '16px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              background: 'linear-gradient(180deg, #ffffff 0%, #ffffff 70%, rgba(244, 243, 248, 0.88) 100%)',
            }}
          >
            {/* Client section - compact */}
            <section>
              <h2 style={{ fontSize: '10px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', marginBottom: '8px' }}>
                Datos del cliente
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ border: '1px solid #e2e8f0', padding: '10px 12px', backgroundColor: 'rgba(248, 250, 252, 0.5)' }}>
                  <p style={{ fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 'bold', marginBottom: '2px' }}>{esPersonaJuridica(presupuesto.cliente_cuit) ? 'Razón Social' : 'Nombre'}</p>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }}>{presupuesto.cliente_nombre}</p>
                </div>
                <div style={{ border: '1px solid #e2e8f0', padding: '10px 12px', backgroundColor: 'rgba(248, 250, 252, 0.5)' }}>
                  <p style={{ fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 'bold', marginBottom: '2px' }}>CUIT</p>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }}>{formatCuit(presupuesto.cliente_cuit)}</p>
                </div>
              </div>
              {/* Admin/Representante - solo si tiene datos */}
              {(presupuesto.admin_nombre || presupuesto.admin_cuit) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                  <div style={{ border: '1px solid #e2e8f0', padding: '8px 12px', backgroundColor: 'rgba(248, 250, 252, 0.3)' }}>
                    <p style={{ fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 'bold', marginBottom: '2px' }}>
                      {esPersonaJuridica(presupuesto.cliente_cuit) ? 'Rep. Legal' : 'Administrador'}
                    </p>
                    <p style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>{presupuesto.admin_nombre || '-'}</p>
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', padding: '8px 12px', backgroundColor: 'rgba(248, 250, 252, 0.3)' }}>
                    <p style={{ fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 'bold', marginBottom: '2px' }}>CUIT {esPersonaJuridica(presupuesto.cliente_cuit) ? 'Rep. Legal' : 'Administrador'}</p>
                    <p style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>{formatCuit(presupuesto.admin_cuit)}</p>
                  </div>
                </div>
              )}
            </section>

            {/* Services table - compact */}
            <section style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
              <h2 style={{ fontSize: '10px', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b' }}>
                Detalle de servicios
              </h2>
              <div style={{ border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#ea7e34', color: 'white', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '10px' }}>
                      <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600 }}>Concepto</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, width: '60px' }}>Cant.</th>
                      {hayDescuentoEnItems && (
                        <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, width: '60px' }}>Dto.%</th>
                      )}
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, width: '90px' }}>Precio</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, width: '90px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      // Lookup service details from maestro
                      const servicioDetails = getServicioDetails(item.servicio_id, servicios)
                      // Use item-level descripcion if available, fallback to maestro
                      const descripcionTexto = item.descripcion ?? servicioDetails.descripcion
                      // Parse descripcion into list items (one per line)
                      const detalleLines = descripcionTexto?.split('\n').filter(line => line.trim()) || []
                      return (
                        <tr key={index} style={{ borderBottom: index < items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                          <td style={{ padding: '8px 10px' }}>
                            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '12px' }}>{servicioDetails.nombre}</div>
                            {detalleLines.length > 0 && (
                              <ul style={{ margin: '4px 0 0 16px', padding: 0, listStyle: 'disc', color: 'rgba(28, 28, 60, 0.72)', fontSize: '10px', lineHeight: 1.4 }}>
                                {detalleLines.map((line, i) => (
                                  <li key={i}>{line}</li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '12px', color: '#1e293b', verticalAlign: 'top' }}>{item.cantidad ?? 0}</td>
                          {hayDescuentoEnItems && (
                            <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '12px', color: '#1e293b', verticalAlign: 'top' }}>
                              {(item.descuento_porcentaje ?? 0) > 0 ? `${item.descuento_porcentaje}%` : '-'}
                            </td>
                          )}
                          <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '12px', color: '#1e293b', verticalAlign: 'top' }}>{formatCurrency(item.precio_unitario ?? 0)}</td>
                          <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '12px', color: '#1e293b', fontWeight: 600, verticalAlign: 'top' }}>{formatCurrency(item.subtotal ?? 0)}</td>
                        </tr>
                      )
                    })}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={hayDescuentoEnItems ? 5 : 4} style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontSize: '12px' }}>
                          No hay items en este presupuesto
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Notes - compact (above totals) */}
            {presupuesto.notas && (
              <section>
                <div style={{ border: '1px solid #e2e8f0', padding: '10px 12px', backgroundColor: 'rgba(255, 237, 213, 0.5)' }}>
                  <p style={{ fontSize: '11px', color: '#475569', lineHeight: 1.5 }}>{presupuesto.notas}</p>
                </div>
              </section>
            )}

            {/* Totals - compact */}
            <section style={{ marginTop: 'auto' }}>
              <div
                style={{
                  position: 'relative',
                  border: '1px solid #e2e8f0',
                  padding: '12px 16px',
                  color: 'white',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(234, 126, 52, 0.9) 120%)',
                }}
              >
                <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotalItems)}</span>
                  </div>
                  {descuentoPorcentaje > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                      <span>Descuento ({descuentoPorcentaje}%)</span>
                      <span>-{formatCurrency(descuento)}</span>
                    </div>
                  )}
                  {ivaPorcentaje > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                      <span>IVA ({ivaPorcentaje}%)</span>
                      <span>{formatCurrency(iva)}</span>
                    </div>
                  )}
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.2)', margin: '4px 0' }}></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '12px' }}>Total propuesto</span>
                    <span style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '0.05em' }}>{formatCurrency(totalFinal || 0)}</span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer - compact */}
          <footer style={{ padding: '12px 24px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {logoUrlFooter && (
                  <img src={logoUrlFooter} alt={estudioNombre} style={{ height: '28px', width: 'auto', opacity: 0.8 }} crossOrigin="anonymous" />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  {!logoUrlFooter && <span style={{ fontWeight: 'bold', color: '#1e293b', fontSize: '12px' }}>{estudioNombre}</span>}
                  {estudioEmail && <span style={{ fontSize: '10px', color: '#64748b' }}>{estudioEmail}</span>}
                  {estudioTelefono && <span style={{ fontSize: '10px', color: '#64748b' }}>Tel: {estudioTelefono}</span>}
                  {estudioSitioWeb && <span style={{ fontSize: '10px', color: '#64748b' }}>{estudioSitioWeb}</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {estudioResponsable && (
                  <>
                    <p style={{ fontSize: '11px', fontWeight: 'bold', color: '#1e293b' }}>Responsable</p>
                    <p style={{ fontSize: '10px', color: '#64748b' }}>{estudioResponsable}</p>
                  </>
                )}
                {estudioMatricula && <p style={{ fontSize: '10px', color: '#64748b' }}>{estudioMatricula}</p>}
                {estudioCuit && <p style={{ fontSize: '10px', color: '#64748b' }}>CUIT: {estudioCuit}</p>}
              </div>
            </div>
            <p style={{ fontSize: '9px', color: '#94a3b8', lineHeight: 1.5 }}>{textoLegal}</p>
          </footer>
        </article>
      </div>
    )
  }
)

PresupuestoTemplate.displayName = 'PresupuestoTemplate'
